import { useSyncExternalStore } from 'react';

import { keys } from 'dibs-ts-utils/exports/keys.ts';

import { setItemInSessionStorage, resetSessionStorage } from './sessionStorage/sessionStorage.ts';
import { resetSierra } from './Sierra/helpers/resetSierra.ts';

import { type SupportChatType, type SupportChatTouchpoint, type PreChatFields } from './types.ts';

type SupportChatState = {
    shouldMount: boolean;
    isEnabled: boolean | null;
    isInitialized: boolean;
    chatType: SupportChatType | null;
    touchpoint: SupportChatTouchpoint | null;
    showChatButtonOnLoad: boolean;
    isLiveAgentAvailable: boolean;
    isButtonVisible: boolean;
    isWindowVisible: boolean;
    hasActiveChat: boolean;
    hasUnreadMessage: boolean;
    preChatFields: PreChatFields;
};

type SupportChatActions = {
    setShouldMount: (shouldMount: boolean) => void;
    setIsEnabled: (isEnabled: boolean) => void;
    setIsInitialized: (isInitialized: boolean) => void;
    setChatType: (chatType: SupportChatType) => void;
    setTouchpoint: (touchpoint: SupportChatTouchpoint) => void;
    setShowChatButtonOnLoad: (showChatButtonOnLoad: boolean) => void;
    setIsLiveAgentAvailable: (isLiveAgentAvailable: boolean) => void;
    setIsButtonVisible: (isButtonVisible: boolean) => void;
    setIsWindowVisible: (isWindowVisible: boolean) => void;
    setHasActiveChat: (hasActiveChat: boolean) => void;
    setHasUnreadMessage: (hasUnreadMessage: boolean) => void;
    setPreChatFields: (preChatFields: PreChatFields) => void;
    resetStore: (options?: { resetStorage: boolean }) => void;
};

type SupportChatStore = SupportChatState & SupportChatActions;

type Listener = (state: SupportChatStore, prevState: SupportChatStore) => void;
type SetState = (
    partial: Partial<SupportChatStore> | ((state: SupportChatStore) => Partial<SupportChatStore>)
) => void;

const initialState: SupportChatState = {
    shouldMount: false,
    isEnabled: null,
    isInitialized: false,
    chatType: null,
    touchpoint: null,
    showChatButtonOnLoad: false,
    isLiveAgentAvailable: false,
    isButtonVisible: false,
    isWindowVisible: false,
    hasActiveChat: false,
    hasUnreadMessage: false,
    preChatFields: {
        topic: '',
        pageType: '',
        itemId: undefined,
        orderId: undefined,
    },
};

const listeners = new Set<Listener>();
let state: SupportChatStore;

const subscribe = (listener: Listener): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const getState = (): SupportChatStore => state;

const setState: SetState = partial => {
    const next = typeof partial === 'function' ? partial(state) : partial;

    if (Object.is(next, state)) {
        return;
    }

    const hasChanges = keys(next).some(key => !Object.is(state[key], next[key]));

    if (!hasChanges) {
        return;
    }

    const prevState = state;
    state = { ...state, ...next };
    listeners.forEach(listener => listener(state, prevState));
};

state = {
    ...initialState,
    setShouldMount: shouldMount => setState({ shouldMount }),
    setIsEnabled: isEnabled => setState({ isEnabled }),
    setIsInitialized: isInitialized => setState({ isInitialized }),
    setChatType: chatType => setState({ chatType }),
    setTouchpoint: touchpoint => {
        setItemInSessionStorage('touchpoint', touchpoint);
        setState({ touchpoint });
    },
    setShowChatButtonOnLoad: showChatButtonOnLoad => {
        setItemInSessionStorage('showChatButtonOnLoad', showChatButtonOnLoad ? 'yes' : 'no');
        setState({ showChatButtonOnLoad });
    },
    setIsLiveAgentAvailable: isLiveAgentAvailable => setState({ isLiveAgentAvailable }),
    setIsButtonVisible: isButtonVisible => {
        setItemInSessionStorage('isButtonVisible', isButtonVisible ? 'yes' : 'no');
        setState({ isButtonVisible });
    },
    setIsWindowVisible: isWindowVisible => {
        setState(currentState => {
            setItemInSessionStorage('isWindowVisible', isWindowVisible ? 'yes' : 'no');
            if (isWindowVisible) {
                setItemInSessionStorage('activeChat', currentState.chatType);
                setItemInSessionStorage('hasUnreadMessage', 'no');
            }
            return {
                isWindowVisible,
                ...(isWindowVisible && { hasActiveChat: true, hasUnreadMessage: false }),
            };
        });
    },
    setPreChatFields: preChatFields =>
        setState(currentState => {
            const newPreChatFields = { ...currentState.preChatFields, ...preChatFields };
            const { pageType, topic, orderId, itemId } = newPreChatFields;
            setItemInSessionStorage('preChatFields', {
                pageType: pageType || '',
                topic: topic || '',
                orderId: orderId || '',
                itemId: itemId || '',
            });
            return { preChatFields: newPreChatFields };
        }),
    setHasActiveChat: hasActiveChat =>
        setState(currentState => {
            setItemInSessionStorage('activeChat', hasActiveChat ? currentState.chatType : null);
            return { hasActiveChat };
        }),
    setHasUnreadMessage: hasUnreadMessage => {
        setItemInSessionStorage('hasUnreadMessage', hasUnreadMessage ? 'yes' : 'no');
        setState({ hasUnreadMessage });
    },
    resetStore: options => {
        resetSierra();
        setState(currentState => {
            if (options?.resetStorage) {
                resetSessionStorage();
                setItemInSessionStorage(
                    'showChatButtonOnLoad',
                    currentState.showChatButtonOnLoad ? 'yes' : 'no'
                );
            }
            return {
                ...initialState,
                chatType: currentState.chatType,
                showChatButtonOnLoad: currentState.showChatButtonOnLoad,
            };
        });
    },
};

const initialStoreState: SupportChatStore = state;

const getInitialState = (): SupportChatStore => initialStoreState;

const useSupportChatStore = Object.assign(
    <TSelected>(selector: (state: SupportChatStore) => TSelected): TSelected =>
        useSyncExternalStore(
            subscribe,
            () => selector(state),
            () => selector(initialStoreState)
        ),
    { getState, getInitialState, setState, subscribe }
);

export { useSupportChatStore };
